/* @license
 * Copyright 2025 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { LinearFilter, NearestFilter, SRGBColorSpace, Texture, } from 'three';
const MIN_DELAY_MS = 100;
/**
 * Parse GIF file directly (no worker needed)
 * Based on gif parser by @gtk2k
 * https://github.com/gtk2k/gtk2k.github.io/tree/master/animation_gif
 */
const parseGIF = (gif) => {
    let pos = 0;
    const delayTimes = [];
    let graphicControl = null;
    const frames = [];
    const disposals = [];
    if (gif[0] === 0x47 &&
        gif[1] === 0x49 &&
        gif[2] === 0x46 && // 'GIF'
        gif[3] === 0x38 &&
        gif[4] === 0x39 &&
        gif[5] === 0x61) {
        // '89a'
        pos += 13 + +!!(gif[10] & 0x80) * Math.pow(2, (gif[10] & 0x07) + 1) * 3;
        const gifHeader = gif.subarray(0, pos);
        while (gif[pos] && gif[pos] !== 0x3b) {
            const offset = pos;
            const blockId = gif[pos];
            if (blockId === 0x21) {
                const label = gif[++pos];
                if ([0x01, 0xfe, 0xf9, 0xff].indexOf(label) !== -1) {
                    if (label === 0xf9) {
                        delayTimes.push((gif[pos + 3] + (gif[pos + 4] << 8)) * 10);
                    }
                    while (gif[++pos])
                        pos += gif[pos];
                    if (label === 0xf9) {
                        graphicControl = gif.subarray(offset, pos + 1);
                        disposals.push((graphicControl[3] >> 2) & 0x07);
                    }
                }
                else {
                    throw new Error("parseGIF: unknown label");
                }
            }
            else if (blockId === 0x2c) {
                pos += 9;
                pos +=
                    1 + +!!(gif[pos] & 0x80) * (Math.pow(2, (gif[pos] & 0x07) + 1) * 3);
                while (gif[++pos])
                    pos += gif[pos];
                if (graphicControl == null) {
                    throw new Error("parseGIF: missing graphic control block");
                }
                const imageData = gif.subarray(offset, pos + 1);
                const blobParts = [
                    gifHeader,
                    graphicControl,
                    imageData
                ];
                frames.push(URL.createObjectURL(new Blob(blobParts)));
            }
            else {
                throw new Error("parseGIF: unknown blockId");
            }
            pos++;
        }
    }
    else {
        throw new Error("parseGIF: no GIF89a");
    }
    return { delayTimes, frames, disposals };
};
const loadImageFromURL = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load GIF frame: ${src}`));
        img.src = src;
    });
};
export class GIFTexture extends Texture {
    constructor(frames, delays, disposals, frameUrls) {
        const canvas = document.createElement('canvas');
        super(canvas);
        this.frameIndex = 0;
        this.isPlaying = false;
        this.frameStartTime = performance.now();
        this.ctx = null;
        this.frameUrls = [];
        this.frames = frames;
        this.delays = delays;
        this.disposals = disposals;
        this.frameUrls = frameUrls;
        if (frames[0]) {
            canvas.width = frames[0].width;
            canvas.height = frames[0].height;
        }
        else {
            canvas.width = 1;
            canvas.height = 1;
        }
        this.ctx = canvas.getContext('2d');
        this.generateMipmaps = false;
        this.isVideoTexture = true;
        this.minFilter = NearestFilter;
        this.colorSpace = SRGBColorSpace;
        this.resetToFirstFrame();
    }
    play() {
        if (this.isPlaying) {
            return;
        }
        this.isPlaying = true;
        this.frameStartTime = performance.now();
    }
    pause() {
        this.isPlaying = false;
    }
    resetToFirstFrame() {
        this.frameIndex = 0;
        this.frameStartTime = performance.now();
        this.drawCurrentFrame(true);
    }
    drawCurrentFrame(force = false) {
        if (!this.ctx || !this.frames[this.frameIndex]) {
            return;
        }
        if (force || this.disposals[this.frameIndex] === 2) {
            this.ctx.clearRect(0, 0, this.image.width, this.image.height);
        }
        this.ctx.drawImage(this.frames[this.frameIndex], 0, 0, this.image.width, this.image.height);
        this.needsUpdate = true;
    }
    update() {
        var _a;
        if (!this.isPlaying || !this.frames.length || !this.ctx) {
            return;
        }
        const now = performance.now();
        const delay = (_a = this.delays[this.frameIndex]) !== null && _a !== void 0 ? _a : MIN_DELAY_MS;
        const elapsed = now - this.frameStartTime;
        const requiredDelay = Math.max(delay, MIN_DELAY_MS);
        if (elapsed < requiredDelay) {
            return;
        }
        this.frameStartTime = now;
        this.frameIndex = (this.frameIndex + 1) % this.frames.length;
        this.drawCurrentFrame();
    }
    dispose() {
        super.dispose();
        this.frames = [];
        this.delays = [];
        this.disposals = [];
        this.ctx = null;
        this.frameUrls.forEach((url) => URL.revokeObjectURL(url));
        this.frameUrls = [];
    }
}
export const createGIFTexture = async (url) => {
    const response = await fetch(url, { mode: 'cors' });
    const arrayBuffer = await response.arrayBuffer();
    const result = parseGIF(new Uint8Array(arrayBuffer));
    const delays = result.delayTimes.map((delay) => Math.max(delay || MIN_DELAY_MS, MIN_DELAY_MS));
    const frames = await Promise.all(result.frames.map(loadImageFromURL));
    const texture = new GIFTexture(frames, delays, result.disposals, result.frames);
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.play();
    return texture;
};
//# sourceMappingURL=gif-texture.js.map